import { Form, Formik } from 'formik';
import { ModalStatusType } from '../../types/common';
import ModalComponent from './modal';
import ModalBodyComponent from './modalBodyComponent';
import ModalFooterComponent from './modalFooterComponent';
import {
  useGetAwardAdjustmentMethod,
  useGetCancelShareTypes,
  useGetTerminationTypes,
  usePutTerminationRules,
  useWealthlaneAwardApi,
} from '../../hooks';
import Heading from '../typography';
import InputText from '../input-text/inputText';
import FormSelect from '../dropdown/formSelect';
import InitialValuesForm from '../../constants/initialValues';
import { TERMINATION_RULES_MODAL_VALIDATION } from '../../utils/validations/validations';
import { useEffect } from 'react';
import { toCamelCase } from '../../utils/global';

interface TerminationRulesModalType {
  isModalOpen: boolean;
  closeModal: () => void;
  modalStatus: ModalStatusType;
  terminationRule: any;
  setTerminationRule: (value: any) => void;
  awardType?: any;
  directEdit?: boolean;
  setModalStatus?: (value: any) => void;
  terminationRuleSetId?: string;
  planId?: string;
  id?: string;
}

const TerminationRulesModal = ({
  isModalOpen,
  closeModal,
  modalStatus,
  terminationRule,
  setTerminationRule,
  awardType,
  directEdit,
  setModalStatus,
  terminationRuleSetId,
  planId,
  id,
}: TerminationRulesModalType): JSX.Element => {
  const { handleRequest } = useWealthlaneAwardApi();
  const { data: terminationTypesOption } = useGetTerminationTypes(handleRequest);
  const { data: awardAdjustmentMethodOption } = useGetAwardAdjustmentMethod(handleRequest);
  const { data: cancelShareTypeOption } = useGetCancelShareTypes(handleRequest);
  const initialValues = {
    terminationRules: terminationRule,
  };
  const { mutate, isLoading } = usePutTerminationRules(
    handleRequest,
    closeModal,
    terminationRuleSetId,
    planId
  );
  const handleSubmit = (values: any) => {
    mutate(values?.terminationRules);
  };

  return (
    <div data-page='terminationRulesModal'>
      <Formik
        enableReinitialize
        initialValues={
          terminationRule?.length > 0 ? initialValues : InitialValuesForm?.terminationRulesModal
        }
        validationSchema={TERMINATION_RULES_MODAL_VALIDATION(awardType)}
        onSubmit={(values) => {
          directEdit ? handleSubmit(values) : setTerminationRule(values?.terminationRules);
          closeModal();
        }}>
        {({
          touched,
          handleChange,
          errors,
          setFieldValue,
          setFieldTouched,
          values,
          setTouched,
          setFieldError,
        }: any) => {
          useEffect(() => {
            if (terminationTypesOption && terminationTypesOption.length > 0) {
              [...terminationTypesOption]
                .sort((a, b) => a.label.localeCompare(b.label))
                .forEach((item: any, index: number) => {
                  setFieldValue(
                    `terminationRules[${index}].participantTerminationTypeId`,
                    item?.value
                  );
                  setFieldValue(
                    `terminationRules[${index}].terminationRuleSetId`,
                    terminationRuleSetId
                  );
                });
            }
          }, [terminationTypesOption, setFieldValue]);

          return (
            <>
              <ModalComponent
                show={isModalOpen}
                closeModal={closeModal}
                loading={false}
                size='xl'
                //   valueEdited={_.isEqual(
                //     values,
                //     modalStatus !== 'Add' ? initialValue : initialData?.data
                //   )}
                customAlertMessage={
                  'Your changes will not be saved. Are you sure you want to close?'
                }
                title={`Termination Rules`}
                id={id}
                children={
                  <Form id='terminationRulesForm'>
                    <ModalBodyComponent id='terminationRulesModalBody'>
                      <div id='terminationRulesHeader' className='grid grid-cols-8 gap-4 mb-4'>
                        <Heading title='' variant='h6' className='col-span-2' />
                        <Heading title='Months' variant='h6' id='months' />
                        <Heading title='Days' variant='h6' id='days' />
                        <Heading
                          title='Cancel share type'
                          variant='h6'
                          className='col-span-2'
                          id='cancelShareType'
                        />
                        <Heading
                          title='Acceleration method'
                          variant='h6'
                          className='col-span-2'
                          id='accelerationMethod'
                        />
                      </div>
                      {terminationTypesOption &&
                        terminationTypesOption?.length > 0 &&
                        [...terminationTypesOption]
                          .sort((a, b) => a.label.localeCompare(b.label))
                          .map((item: any, index: number) => {
                            return (
                              <div
                                id={`terminationRule.${index}`}
                                className='grid grid-cols-8 gap-4 mb-4 items-center'>
                                <div id={`terminationType.${index}`} className='col-span-2'>
                                  <Heading
                                    title={`${item?.label}`}
                                    variant='h6'
                                    id={`${toCamelCase(item?.label)}`}
                                  />
                                </div>
                                <InputText
                                  type='text'
                                  className='mb-0'
                                  name={`terminationRules[${index}].expirationInMonths`}
                                  id={`${toCamelCase(item?.label)}ExpirationInMonths`}
                                  onChange={handleChange}
                                  errors={errors}
                                  touched={touched}
                                  errorValue={errors?.terminationRules?.[index]?.expirationInMonths}
                                  touchedValue={
                                    touched?.terminationRules?.[index]?.expirationInMonths
                                  }
                                  inputType={modalStatus}
                                  disabled={
                                    modalStatus === 'View' || (!awardType?.includes('Options') && !awardType?.includes('SAR'))
                                  }
                                  required
                                />
                                <InputText
                                  type='text'
                                  className='mb-0'
                                  name={`terminationRules[${index}].expirationInDays`}
                                  id={`${toCamelCase(item?.label)}ExpirationInDays`}
                                  onChange={(e: any) => {
                                    item?.label === 'Death'
                                      ? setFieldValue('deathExpirationOption', e.target.value)
                                      : item?.label === 'Disability'
                                      ? setFieldValue('disablityExpirationOption', e.target.value)
                                      : item?.label === 'Retirement'
                                      ? setFieldValue('retirementExpirationOption', e.target.value)
                                      : item?.label === 'Voluntary'
                                      ? setFieldValue('volutaryTerminationOption', e.target.value)
                                      : item?.label === 'Involuntary termination'
                                      ? setFieldValue('involutaryTerminationOption', e.target.value)
                                      : item?.label === 'With a cause'
                                      ? setFieldValue('withACause', e.target.value)
                                      : null;

                                    handleChange(e);
                                  }}
                                  errors={errors}
                                  touched={touched}
                                  errorValue={errors?.terminationRules?.[index]?.expirationInDays}
                                  touchedValue={
                                    touched?.terminationRules?.[index]?.expirationInDays
                                  }
                                  inputType={modalStatus}
                                  disabled={
                                    modalStatus === 'View' || (!awardType?.includes('Options') && !awardType?.includes('SAR'))
                                  }
                                  required
                                />
                                <div className='col-span-2'>
                                  <FormSelect
                                    name={`terminationRules[${index}].cancelShareTypeId`}
                                    id={`${toCamelCase(item?.label)}CancelShareTypeId`}
                                    viewName='cancelShareTypeId'
                                    containerClassName='formGroup mb-0'
                                    onChange={handleChange}
                                    errors={errors}
                                    touched={touched}
                                    errorValue={
                                      errors?.terminationRules?.[index]?.cancelShareTypeId
                                    }
                                    touchedValue={
                                      touched?.terminationRules?.[index]?.cancelShareTypeId
                                    }
                                    options={cancelShareTypeOption}
                                    values={values}
                                    arrValue={values?.terminationRules[index]?.cancelShareTypeId}
                                    setFieldValue={setFieldValue}
                                    inputType={modalStatus}
                                    disabled={modalStatus === 'View'}
                                    required
                                  />
                                </div>
                                <div className='col-span-2'>
                                  <FormSelect
                                    name={`terminationRules[${index}].awardAdjustmentMethodId`}
                                    id={`${toCamelCase(item?.label)}AwardAdjustmentMethodId`}
                                    viewName='awardAdjustmentMethodId'
                                    containerClassName='formGroup mb-0'
                                    onChange={(e: any) => {
                                      handleChange(e);
                                      setFieldValue(
                                        `terminationRules[${index}].participantTerminationTypeId`,
                                        item?.value
                                      );
                                    }}
                                    errors={errors}
                                    touched={touched}
                                    errorValue={
                                      errors?.terminationRules?.[index]?.awardAdjustmentMethodId
                                    }
                                    touchedValue={
                                      touched?.terminationRules?.[index]?.awardAdjustmentMethodId
                                    }
                                    options={awardAdjustmentMethodOption}
                                    values={values}
                                    arrValue={
                                      values?.terminationRules[index]?.awardAdjustmentMethodId
                                    }
                                    setFieldValue={setFieldValue}
                                    inputType={modalStatus}
                                    disabled={modalStatus === 'View'}
                                    required
                                  />
                                </div>
                              </div>
                            );
                          })}
                    </ModalBodyComponent>
                    <ModalFooterComponent
                      id='terminationRulesModalFooter'
                      modalStatus={modalStatus}
                      checkError={false}
                      loading={isLoading}
                      isEditShow={true}
                      // isButton={true}
                      buttonTitle={!directEdit && 'Add'}
                      // isTmc={modalStatus === 'View' && id ? true : false}
                      onCloseModal={() => {
                        closeModal();
                      }}
                      onClickButton={() => {
                        [...terminationTypesOption]
                          .sort((a, b) => a.label.localeCompare(b.label))
                          .forEach((item: any, index: number) => {
                            setFieldTouched(
                              `terminationRules[${index}].participantTerminationTypeId`,
                              true,
                              false
                            );
                            setFieldTouched(
                              `terminationRules[${index}].expirationInMonths`,
                              true,
                              false
                            );
                            setFieldTouched(
                              `terminationRules[${index}].expirationInDays`,
                              true,
                              false
                            );
                            setFieldTouched(
                              `terminationRules[${index}].cancelShareTypeId`,
                              true,
                              false
                            );
                            setFieldTouched(
                              `terminationRules[${index}].awardAdjustmentMethodId`,
                              true,
                              false
                            );
                          });
                      }}
                      valueChanged={false}
                      onEditModal={() => {
                        setModalStatus('Edit');
                      }}
                      customAlertMessage={
                        'Your changes will not be saved. Are you sure you want to close?'
                      }
                    />
                  </Form>
                }
              />
            </>
          );
        }}
      </Formik>
    </div>
  );
};

export default TerminationRulesModal;
